import React, { useState } from "react";
import { Search, Loader2, CheckCircle, XCircle, FileText, User, Phone, Car, Calendar, MapPin } from "lucide-react";
import Swal from "sweetalert2";

const BOOKING_BASE = "/api/booking/";

export default function TrackRepairshopBookingForm() {
  const [bookingCode, setBookingCode] = useState("");
  const [bookingData, setBookingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!bookingCode.trim()) {
      Swal.fire({
        title: "خطا",
        text: "لطفاً کد پیگیری را وارد کنید.",
        icon: "warning",
        confirmButtonColor: "#2b2b2b",
        confirmButtonText: "متوجه شدم"
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${BOOKING_BASE}Booking/GetBooking?BookingCode=${bookingCode.trim()}`);

      const text = await res.text();

      if (!res.ok) {
        throw new Error(text || "نوبت یافت نشد.");
      }

      const data = JSON.parse(text);
      if (data && data.BookingCode) {
        // Workaround: Fetch real vehicle model using VIN because GetBooking sometimes returns hardcoded incorrect model
        try {
          if (data.BookingVinNumber) {
            const chassisRes = await fetch(`${BOOKING_BASE}Booking/getSubscriberChassisAllocation?VinNumber=${data.BookingVinNumber}`);
            if (chassisRes.ok) {
              const chassisText = await chassisRes.text();
              if (chassisText) {
                const chassisData = JSON.parse(chassisText);
                data.RealVehicleModelName = chassisData.ChassisVehicleModelName || chassisData.ChassisVehicleName;
              }
            }
          }
        } catch (e) {
          console.error("Failed to fetch real vehicle model:", e);
        }

        setBookingData(data);
      } else {
        throw new Error("نوبت یافت نشد.");
      }
    } catch (err) {
      Swal.fire({
        title: "خطا",
        text: err.message || "ارتباط با سرور برقرار نشد.",
        icon: "error",
        confirmButtonColor: "#2b2b2b",
        confirmButtonText: "متوجه شدم"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelBooking = () => {
    Swal.fire({
      title: "لغو نوبت",
      text: "برای لغو نوبت، لطفاً شماره شاسی (VIN) خودرو را وارد کنید:",
      input: "text",
      inputPlaceholder: "شماره شاسی / VIN",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2b2b2b",
      confirmButtonText: "بله، لغو شود",
      cancelButtonText: "انصراف",
      showLoaderOnConfirm: true,
      preConfirm: async (vinInput) => {
        if (!vinInput || vinInput.trim() === '') {
          Swal.showValidationMessage("لطفاً شماره شاسی را وارد کنید");
          return false;
        }
        if (vinInput.trim().toUpperCase() !== (bookingData.BookingVinNumber || "").toUpperCase()) {
          Swal.showValidationMessage("VIN وارد شده صحیح نمی باشد");
          return false;
        }
        try {
          const res = await fetch(`${BOOKING_BASE}Booking/CancelBooking?BookingCode=${bookingData.BookingCode}&VinNumber=${vinInput.trim()}`);
          if (!res.ok) {
            const errText = await res.text();
            throw new Error(errText || "خطا در ارتباط با سرور");
          }
          return true;
        } catch (error) {
          Swal.showValidationMessage(`خطا: ${error.message}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        const trackingCode = bookingData?.BookingCode || bookingData?.TrackingCode || "---";
        const mobile = bookingData?.BookingMobile || bookingData?.Mobile || "";

        const smsText = `مشتری گرامی، نوبت تعمیرگاه شما با کد پیگیری ${trackingCode} لغو گردید؛ جهت ثبت نوبت جدید میتوانید مجدداً با ما در ارتباط باشید.\nبا تشکر شرکت برساوش تجارت نیک`;

        if (mobile) {
          try {
            fetch("/api/sms/send_sms/", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                to: mobile,
                message: smsText
              })
            }).catch(err => console.error("SMS fetch error:", err));
          } catch (smsError) {
            console.error("SMS sending failed:", smsError);
          }
        }

        Swal.fire({
          title: "موفق",
          text: "نوبت شما با موفقیت لغو شد.",
          icon: "success",
          confirmButtonText: "تایید",
          confirmButtonColor: "#2b2b2b",
        });
        setBookingData(null);
        setBookingCode("");
      }
    });
  };

  return (
    <div dir="rtl" className="w-full mx-auto">
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-lg shadow-slate-200/50">
        <div className="px-5 py-6 sm:px-7 flex flex-col gap-6">

          {/* Header */}
          <div>
            <h3 className="text-xl font-black text-slate-800 mb-2">لغو و مشاهده نوبت</h3>
            <p className="text-sm text-slate-500">
              برای مشاهده وضعیت نوبت یا لغو آن، کد پیگیری پیامک شده را وارد کنید.
            </p>
          </div>

          {/* Search Input */}
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={bookingCode}
                onChange={(e) => setBookingCode(e.target.value)}
                placeholder="مثال: 10-1002-1001"
                dir="ltr"
                className="w-full px-4 py-3 pl-12 rounded-xl border-2 text-sm transition-all outline-none bg-slate-50 border-slate-200 focus:bg-white focus:border-[#2b2b2b] focus:ring-1 focus:ring-[#2b2b2b] text-center sm:text-left"
              />
              <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-[#2b2b2b] hover:bg-neutral-800 disabled:bg-slate-300 text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-neutral-900/20 transition-all flex items-center justify-center gap-2 sm:w-auto w-full active:scale-95"
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Search size={18} />
              )}
              جستجو
            </button>
          </form>

          {/* Results */}
          {bookingData && (
            <div className="mt-2 border-t border-slate-100 pt-6 flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center gap-2 text-slate-800 font-bold">
                  <CheckCircle size={20} className="text-[#2b2b2b]" />
                  وضعیت نوبت:
                </div>
                <div className="bg-white px-3 py-1 rounded-lg text-sm font-bold text-slate-800 shadow-sm border border-gray-200">
                  {bookingData.BookingStatus}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4">
                <InfoItem
                  icon={<User size={16} />}
                  label="نام و نام خانوادگی"
                  value={`${bookingData.BookingFirstName || ''} ${bookingData.BookingLastName || ''}`}
                />
                <InfoItem
                  icon={<Phone size={16} />}
                  label="شماره موبایل"
                  value={bookingData.BookingMobile}
                />
                <InfoItem
                  icon={<Car size={16} />}
                  label="مدل خودرو"
                  value={bookingData.RealVehicleModelName || bookingData.BookingVehicleModelName || bookingData.BookingVehicleModel}
                />

                <InfoItem
                  icon={<Calendar size={16} />}
                  label="تاریخ و زمان"
                  value={`${bookingData.StrBookingDate} - ساعت ${bookingData.StrBookingTime}`}
                />
                <InfoItem
                  icon={<MapPin size={16} />}
                  label="آدرس شعبه"
                  value={bookingData.BookingListFullAddress || 'ثبت نشده'}
                />
              </div>

              <div className="flex mt-4 pt-6 border-t border-slate-100">
                <button
                  onClick={handleCancelBooking}
                  className="w-full bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95"
                >
                  <XCircle size={18} />
                  لغو نوبت
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </span>
      <span className="text-sm font-bold text-slate-800 pr-5">
        {value || '-'}
      </span>
    </div>
  );
}
